import React, { useState } from "react";
import { Flex, Text, Link } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { globalEnv } from "~/global-env";
import { useAmlLuxFeatureFlag } from "~/pages/Compliance/flags";
import { toDbId } from "~/utils/graphql-helpers";
import { ProfileField } from "../ProfileField";
import { addressValue } from "../ProfileField/styles_vanilla.css";
import { profileFieldsContainer, revealButton, revealElement } from "./styles_vanilla.css";

export type ProfileFieldsRendererProps = {
  profileName: string;
  legalName: string;
  dateLabel: string;
  formattedDate: string;
  address: string;
  ssn: string;
  isIndividual: boolean;
  hasSSN: boolean;
  profileId?: number;
  positionIds?: string[];
  arenaIds?: number[];
  investorProfileName?: string;
  investorProfileId?: number;
};

const decodePositionDbIds = (positionIds: string[]): number[] =>
  positionIds
    .map((id) => {
      try {
        const dbId = toDbId(id);
        return Number.isNaN(dbId) ? undefined : dbId;
      } catch {
        return undefined;
      }
    })
    .filter((id): id is number => id !== undefined);

const buildCanopyProfilesUrl = ({
  profileId,
  profileName,
}: {
  profileId?: number;
  profileName?: string;
}): string => {
  const base = `/canopy${globalEnv.complianceRoute}/profiles`;
  const params = new URLSearchParams();
  if (profileId != null) params.set("profileId", String(profileId));
  if (profileName && profileName !== "--") params.set("name", profileName);

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

const buildCanopyPositionsUrl = (positionIds: string[], arenaIds: number[]): string => {
  const positionDbIds = decodePositionDbIds(positionIds);
  const base = `/canopy${globalEnv.complianceRoute}/positions`;

  const params = new URLSearchParams();
  if (arenaIds.length > 0) params.set("arenaIds", arenaIds.join(","));
  if (positionDbIds.length > 0) params.set("positionIds", positionDbIds.join(","));

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

export const ProfileFieldsRenderer: React.FC<ProfileFieldsRendererProps> = ({
  profileName,
  legalName,
  dateLabel,
  formattedDate,
  address,
  ssn,
  isIndividual,
  hasSSN,
  profileId,
  positionIds = [],
  arenaIds = [],
  investorProfileName,
  investorProfileId,
}) => {
  const amlLuxEnabled = useAmlLuxFeatureFlag();
  const [isSSNRevealed, setIsSSNRevealed] = useState(false);

  const handleRevealSSN = () => setIsSSNRevealed(!isSSNRevealed);

  const positionCount = positionIds.length;
  const positionsUrl = buildCanopyPositionsUrl(positionIds, arenaIds);
  const profileUrl = buildCanopyProfilesUrl({ profileId, profileName });

  return (
    <Flex flexDirection="column" className={profileFieldsContainer}>
      <ProfileField
        label="Profile"
        value={
          <Link
            href={profileUrl}
            shouldOpenInNewTab
            rel="noopener noreferrer"
            icon="external-link"
            iconPosition="right"
            data-testid="profile-link"
          >
            {profileName}
          </Link>
        }
      />

      <ProfileField label="Legal name" value={<Text variant="regular-md">{legalName}</Text>} />

      <ProfileField
        label={dateLabel}
        value={<Text variant="regular-md">{formattedDate}</Text>}
      />

      <ProfileField
        label="Address"
        value={
          <Text variant="regular-md" className={addressValue}>
            {address}
          </Text>
        }
      />

      {isIndividual && (
        <ProfileField
          label="Social security number"
          value={
            <Flex alignItems="center" gap="4px">
              <Text variant="regular-md">
                {hasSSN && isSSNRevealed ? ssn : hasSSN ? "••••••" : "--"}
              </Text>
              {hasSSN && (
                <Flex
                  alignItems="center"
                  gap="4px"
                  onClick={handleRevealSSN}
                  className={revealButton}
                  data-testid="reveal-ssn-button"
                >
                  <Icon
                    type={isSSNRevealed ? "eye-slash" : "eye"}
                    variant="solid"
                    className={revealElement}
                  />
                  <Text variant="regular-md" className={revealElement}>
                    {isSSNRevealed ? "Hide" : "Reveal"}
                  </Text>
                </Flex>
              )}
            </Flex>
          }
        />
      )}

      {amlLuxEnabled && (
        <ProfileField
          label="Positions"
          value={
            positionCount > 0 ? (
              <Link
                href={positionsUrl}
                shouldOpenInNewTab
                rel="noopener noreferrer"
                icon="external-link"
                iconPosition="right"
                data-testid="positions-link"
              >
                {positionCount}
              </Link>
            ) : (
              <Text variant="regular-md" data-testid="positions-count">
                {positionCount}
              </Text>
            )
          }
        />
      )}

      {investorProfileId != null && (
        <ProfileField
          label="Investor Profile"
          value={
            <Link
              href={buildCanopyProfilesUrl({
                profileId: investorProfileId,
                profileName: investorProfileName,
              })}
              shouldOpenInNewTab
              rel="noopener noreferrer"
              icon="external-link"
              iconPosition="right"
              data-testid="investor-profile-link"
            >
              {investorProfileName ?? "--"}
            </Link>
          }
        />
      )}
    </Flex>
  );
};
